import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

import { Box } from '@mui/material';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { SCUILoadingLinear } from '../../atomic/atoms';
import { SummaryDetailV2, SummaryList } from './components';

import { SummaryCommandV2 } from '../../services/command';
import { SendToAxaptaStatus } from './enum/SummaryEnums';
import { SV } from './summaryV2.classes';

const resolveRefreshedSummary = (summaries: any[], previousSummary: any) => {
    if (!previousSummary?.id) {
        return previousSummary;
    }
    return summaries.find((item: any) => item.id === previousSummary.id) ?? previousSummary;
};

export const SummaryV2 = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [summaryList, setSummaryList] = useState<any[]>([]);
    const [selectedSummary, setSelectedSummary] = useState<any>({});
    const [startDate, setStartDate] = useState<string | Date>('');
    const [endDate, setEndDate] = useState<string | Date>('');
    const [isSearched, setIsSearched] = useState<boolean>(false);
    const [status, setStatus] = useState<SendToAxaptaStatus>(SendToAxaptaStatus.All);

    const { t } = useTranslation();

    const handleSearch = async (options?: { refreshSelectedSummary?: boolean }) => {
        setIsLoading(true);
        setIsSearched(true);
        try {
            const res = await SummaryCommandV2.GetSummaryList({
                startDate: startDate,
                endDate: endDate,
                sendToAxaptaStatus: status,
            });
            const summaries = res?.summaries || [];
            setSummaryList(summaries);

            if (options?.refreshSelectedSummary) {
                setSelectedSummary((prev: any) => resolveRefreshedSummary(summaries, prev));
            }
        } catch {
            Swal.fire(t('general.anErrorOccured'), '', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAxapta = async (id: any) => {
        setIsLoading(true);
        try {
            const res = await SummaryCommandV2.AxaptaTransferSummary({
                consolidationId: id,
            });
            if (res) {
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    showConfirmButton: false,
                    timer: 1500,
                });
                await handleSearch();
            }
        } catch {
            Swal.fire(t('general.anErrorOccured', '', 'error'));
            setIsLoading(false);
        }
    };
    return (
        <SCUIContent className={SV.page}>
            <Box className={SV.root}>
                <Box className={SV.breadcrumb}>
                    <SCUIBreadCrumb
                        onlyTitle={t('summary.title')}
                        style={{ cursor: 'pointer' }}
                        components={[
                            {
                                title: t('summary.title'),
                                link: '/summary-list',
                                onClick: () => {
                                    setSelectedSummary({});
                                },
                            },
                            ...(selectedSummary?.id
                                ? [
                                      {
                                          title: t('summary.summaryDetail'),
                                          link: '/summary-list',
                                      },
                                  ]
                                : []),
                        ]}
                    />
                </Box>
                {isLoading && (
                    <Box sx={{ margin: '24px 0 24px 0 ' }} className={SV.loading}>
                        <SCUILoadingLinear display={isLoading} />
                    </Box>
                )}
                <SummaryList
                    setIsLoading={setIsLoading}
                    summaryList={summaryList}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    handleAxapta={handleAxapta}
                    setSummaryList={setSummaryList}
                    setSelectedSummary={setSelectedSummary}
                    handleSearch={handleSearch}
                    selectedSummary={selectedSummary}
                    isSearched={isSearched}
                    status={status}
                    setStatus={setStatus}
                />
                <SummaryDetailV2
                    summary={selectedSummary}
                    setSummary={setSelectedSummary}
                    getSummaryList={() => handleSearch({ refreshSelectedSummary: true })}
                />
            </Box>
        </SCUIContent>
    );
};
